import { Injectable } from '@angular/core';
import { arr_map, blank, data_get, fallback, filled, safely_encode_to_json, spf } from '../shared/utils/common';

@Injectable({
  providedIn: 'root'
})

export class LocalStorageService {

  /**
   * A prefix that is applied to all data stored by FieldmagiC
   * Cloud in the browser. This will help us differentiate our
   * own data from other apps that  also use local storage.
   *
   * @type {string}
   */
  private _prefix: string = 'FMCloud:';

  constructor() { }

  /**
   * Sets data within the browser's local storage.
   *
   * @param {string}  key
   * @param {any}     value
   *
   * @returns {void}
   */
  setItem(key: string, value: any): void {
    value = (typeof value == 'object') ? JSON.stringify(value) : value;

    localStorage.setItem(this._createStorageKey(key), value);
  }

  /**
   * Similar to LocalStorageService::setItem but removes the given
   * item from local storage first.
   *
   * @param {string}  key
   * @param {any}     value
   *
   * @returns {void}
   */
  replaceItem(key: string, value: any): void {
    this.removeItem(key);
    this.setItem(key, value);
  }

  /**
   * Similar to setItem, but converts the given value
   * into a JSON string first, before storage.
   *
   * @param {string}  key
   * @param {object}  value
   *
   * @returns {void}
   */
  setJsonItem(key: string, value: object): void {
    localStorage.setItem(this._createStorageKey(key), fallback(safely_encode_to_json(value), {
      to: () => 'null',
    }));
  }

  /**
   * Similar to LocalStorageService::setJsonItem but removes the given
   * item from local storage first.
   *
   * @param {string}  key
   * @param {object}  value
   *
   * @returns {void}
   */
  replaceJsonItem(key: string, value: object): void {
    this.removeItem(key);
    this.setJsonItem(key, value);
  }

  /**
   * Get an Fieldmagic data entry from the browser's
   * local storage.
   *
   * @param {string} key
   *
   * @returns {any}
   */
  getItem(key: string): any {
    return localStorage.getItem(this._createStorageKey(key));
  }

  /**
   * Similar to getItem, but parses the fetched data
   * and converts it into a JSON object.
   *
   * @param {string} key
   *
   * @returns {object|array}
   */
  getJsonItem(key: string): any {
    let jsonItem = localStorage.getItem(this._createStorageKey(key));
    return jsonItem !== null ? JSON.parse(jsonItem) : null;
  }

  /**
   * Removes a Fieldmagic data entry from the browser's
   * local storage.
   *
   * @param {string} key
   *
   * @returns {void}
   */
  removeItem(key: string): void {
    localStorage.removeItem(this._createStorageKey(key));
  }

  /**
   * Get an Fieldmagic data entry from the browser's
   * local storage.
   *
   * @param {string} key
   *
   * @returns {any}
   */
  hasItem(key: string): any {
    var item = localStorage.getItem(this._createStorageKey(key));

    return item !== undefined && item !== null;
  }

  /**
   * Removes Fieldmagic's data from the browser's local storage.
   *
   * @returns {void}
   */
  clear(opts: {
    only?: string[],
  } = {}): void {
    if (filled(data_get(opts, 'only'))) {
      opts = {
        ... opts,
        only: arr_map(opts.only, {
          transformer: (key) => spf('%s%s', {
            args: [this._prefix, key],
          }),
        }),
      };
    }

    if (blank(data_get(opts, 'only'))) {
      opts = {
        ... opts,
        only: Object.keys(localStorage),
      };
    }

    for (const key of opts.only) {
      if (key.indexOf(this._prefix) < 0) {
        continue;
      }

      localStorage.removeItem(key);
    }
  }

  /**
   * Creates the identification keys used for storing
   * items in local storage.
   *
   * @param {string} subject
   */
  private _createStorageKey(subject: string): string {
    return `${this._prefix}${subject}`;
  }

  /**
   * Replaces / Adds / Removes value of the current client in local storage
   *
   * @param arKey
   * @param arValue
   * @param arRemove
   *
   */
  public saveValueInCurrentClient(arKey: Array<string>, arValue: Array<string | object | boolean>, arRemove: Array<string> = []) {
    let objCurrentClient = JSON.parse(this.getItem('current_client'));

    if (objCurrentClient) {
      arKey.forEach((key, index)  => {
        objCurrentClient[key] = arValue[index];
      });

      arRemove.forEach((key)  => {
        delete objCurrentClient[key];
      });

      this.setItem('current_client', JSON.stringify(objCurrentClient))
    }
  }

}
